"use client"

import { useCallback, useState } from "react"
import { TriangleAlert, Upload } from "lucide-react"

import { parseExistingSnippet } from "@/lib/parse-existing-snippet"
import {
  FormValues,
  MAX_DESCRIPTION_LENGTH,
  MAX_SCOPE_LENGTH,
  MAX_SNIPPET_LENGTH,
  MAX_TAB_TRIGGER_LENGTH,
} from "@/lib/schema"
import { formatDefinitions } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { DialogHelper } from "@/components/dialog-helper"

interface PasteSnippetDialogProps {
  onParse: (data: FormValues) => void
}

export function PasteSnippetDialog({ onParse }: PasteSnippetDialogProps) {
  const [open, setOpen] = useState(false)
  const [pasteInput, setPasteInput] = useState("")
  const [error, setError] = useState<string | null>(null)

  const handlePasteSnippet = useCallback(() => {
    if (!pasteInput.trim()) {
      setError("Please paste a snippet to parse")
      return
    }

    const parsed = parseExistingSnippet(pasteInput)
    if (!parsed) {
      setError(
        `Unable to parse the snippet. Please ensure it's in a valid format (${Object.values(
          formatDefinitions
        )
          .map((format) => format.label)
          .join(", ")}).`
      )
      return
    }

    const validationErrors: string[] = []

    if (parsed.description.length > MAX_DESCRIPTION_LENGTH) {
      validationErrors.push(
        `Description exceeds maximum length of ${MAX_DESCRIPTION_LENGTH} characters (${parsed.description.length}).`
      )
    }

    if (parsed.tabTrigger.length > MAX_TAB_TRIGGER_LENGTH) {
      validationErrors.push(
        `Tab trigger exceeds maximum length of ${MAX_TAB_TRIGGER_LENGTH} characters (${parsed.tabTrigger.length}).`
      )
    }

    if (parsed.snippet.length > MAX_SNIPPET_LENGTH) {
      validationErrors.push(
        `Snippet exceeds maximum length of ${MAX_SNIPPET_LENGTH} characters (${parsed.snippet.length}).`
      )
    }

    if (parsed.scope && parsed.scope.length > MAX_SCOPE_LENGTH) {
      validationErrors.push(
        `Scope exceeds maximum length of ${MAX_SCOPE_LENGTH} characters (${parsed.scope.length}).`
      )
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(" "))
      return
    }

    onParse({
      description: parsed.description,
      tabTrigger: parsed.tabTrigger,
      snippet: parsed.snippet,
      format: parsed.format,
      scope: parsed.scope,
    })
    setOpen(false)
    setPasteInput("")
    setError(null)
  }, [pasteInput, onParse])

  const handleOpenChange = useCallback((value: boolean) => {
    setOpen(value)
    if (!value) {
      setPasteInput("")
      setError(null)
    }
  }, [])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPasteInput(e.target.value)
      if (error) {
        setError(null)
      }
    },
    [error]
  )

  return (
    <DialogHelper
      trigger={
        <Button variant="outline" size="icon-sm">
          <Upload className="h-4 w-4" />
          <span className="sr-only">Paste snippet</span>
        </Button>
      }
      title="Paste Existing Snippet"
      description={`Paste your existing snippet code here to edit it. Supports ${Object.values(
        formatDefinitions
      )
        .map((format) => format.label)
        .join(", ")} formats.`}
      open={open}
      setOpen={handleOpenChange}
      className="max-w-3xl"
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Textarea
            value={pasteInput}
            onChange={handleInputChange}
            placeholder="Paste your snippet here..."
            className={`h-[300px] resize-none font-mono text-sm ${
              error ? "border-destructive focus-visible:ring-destructive" : ""
            }`}
            aria-invalid={!!error}
            aria-describedby={error ? "error-message" : undefined}
          />
          {error && (
            <div
              id="error-message"
              className="text-destructive flex items-start gap-2 text-sm"
              role="alert"
            >
              <TriangleAlert className="text-destructive size-4" />
              <span>{error}</span>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handlePasteSnippet} disabled={!pasteInput.trim()}>
            Parse & Load
          </Button>
        </div>
      </div>
    </DialogHelper>
  )
}
